<template>
  <highcharts style="width: 100%;height: 15vh" class="fit-highchart perf-hist-chart" :options="chartOption" ref="highstockchart" />
</template>

<script>
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import cloneDeep from 'lodash/cloneDeep';
import { chartColorSet, chartColorSetBlack } from '@/constants/chart-options';
import { convertValuByUnit } from '@/utils/number';
import { getDateToString } from '@/utils/date';
import { isEmpty } from '@/utils/basic';
import DashboardConst from '@/constants/dashboard';

const defaultChartOption = {
  chart: {
    type: 'line',
    backgroundColor: 'transparent',
    // height: 120, // 차트 높이 지정
  },
  title: false,
  credits: false,
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  xAxis: {
    type: 'datetime',
    minRange: 5 * 60 * 1000,
    labels: {
      format: '{value: %m-%d %H:%M:%S}',
      style: {
        color: '#888',
        fontSize: '9px',
      },
    },
    crosshair: true,
    lineColor: '#e6e6e6',
    tickColor: '#e6e6e6',
    gridLineColor: '#e6e6e6',
  },
  yAxis: {
    opposite: false,
    labels: false,
    title: false,
    minTickInterval: 0,
    startOnTick: false,
    gridLineColor: '#e6e6e6',
  },
  rangeSelector: false,
  navigator: {
    outlineColor: '#ddd',
    xAxis: {
      gridLineColor: '#e6e6e6',
    },
  },
  scrollbar: false,
  // scrollbar: {
  //   trackBackgroundColor: '#f0f3f6',
  //   barBackgroundColor: '#9da9c1',
  //   barBorderColor: '#9da9c1',
  //   height: 8,
  // },
  plotOptions: {
    series: {
      showInNavigator: true,
      lineWidth: 1.8,
      marker: {
        radius: 2,
      },
    },
  },
  legend: false,
  series: [],
  colors: chartColorSet,
  tooltip: {
    split: false,
    shared: true,
    valueDecimals: 2,
    useHTML: true,
  },
};

// min, max 동일하게 사용 후 하단에서 backgroundColor 변경
const defaultMinMaxLabel = {
  x: null,
  y: null,
  marker: {
    enabled: true,
    symbol: 'diamond',
    radius: 4,
  },
  dataLabels: {
    enabled: true,
    shape: 'callout',
    className: 'min-data-label ',
    backgroundColor: '#568ad3',
    borderRadius: 10,
    useHTML: true,
    padding: 3,
    style: {
      fontSize: '11px',
      color: '#FFFFFF',
      fontWeight: 'normal',
      textOutline: 'none',
    },
    y: -8,
    formatter: null,
  },
};
export default {
  name: 'WPerfRootChart',
  inject: {
    themeState: {
      default: () => {},
    },
  },
  data() {
    return {
      chartOption: cloneDeep(defaultChartOption),
      chartData: null,
    };
  },
  props: {
    viewType: {
      type: String,
      default: null,
    },
    chartType: {
      type: String,
      default: 'PerfHist',
    },
  },
  computed: {
    refsChartComponent() {
      return this.$refs.highstockchart || null;
    },
    isBlack() {
      return (!isEmpty(this.themeState) && this.themeState.theme === DashboardConst.THEME.BLACK);
    },
  },
  watch: {
    themeState: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!isEmpty(val)) {
          this.drawChart();
          this.$nextTick(() => {
            this.$refs.highstockchart.chart.redraw();
          });
        }
      },
    },
    viewType: {
      immediate: true,
      handler(val) {
        this.changeChartType(val);
      },
    },
  },
  methods: {
    setData(newChartData) {
      this.chartData = newChartData;
      // 2018.10.30
      // chartData가 null이어도 스켈레톤 형식으로 데이터가 오기때문에 차트형태는 보여줘야 함.
      // 따라서 cloneDeep 또는 널 데이터를 억지로 넣지 않아도 되기 때문에 drawChart에서 newChartData = null 처리 생햑
      this.chartOption.series = this.chartData;
      if (!isEmpty(this.viewType)) {
        this.chartOption.chart.type = this.viewType;
      }
      this.$nextTick(() => {
        this.drawChart();
      });
    },
    drawChart() {
      this.setChartTooltip();
      this.makeMinMaxFlag(this.chartData);
      // this.chartOption.chart.height = this.$el.clientHeight;
      this.chartOption.colors = (this.isBlack) ? chartColorSetBlack : chartColorSet;
      this.chartOption.xAxis.lineColor = (this.isBlack) ? '#414755' : '#f3f3f5';
      this.chartOption.xAxis.tickColor = (this.isBlack) ? '#414755' : '#f3f3f5';
      this.chartOption.xAxis.tickColor = (this.isBlack) ? '#414755' : '#f3f3f5';
      this.chartOption.yAxis.gridLineColor = (this.isBlack) ? '#414755' : '#f3f3f5';
      this.chartOption.navigator.outlineColor = (this.isBlack) ? '#414755' : '#f3f3f5';
      this.chartOption.navigator.xAxis.gridLineColor = (this.isBlack) ? '#414755' : '#f3f3f5';
    },
    setChartTooltip() {
      if (this.chartType === 'LogHist') {
        this.chartOption.tooltip = {
          xDateFormat: '%Y-%m-%d %H:%M:%S',
          split: false,
          shared: true,
          valueDecimals: 0,
          valueSuffix: '개',
        };
      } else {
        this.chartOption.tooltip.formatter = function tooltipFormatter() {
          let s = `${getDateToString(this.x)}`;
          this.points.forEach((value) => {
            const convertedValue = convertValuByUnit(value.y, isEmpty(value.series.userOptions.rscInfo) ? '' : value.series.userOptions.rscInfo.rscUnit, true);
            s += `<br/><span style="color:${value.color}">\u25CF</span>`;
            s += `${isEmpty(value.series.userOptions.rscInfo) ? '-' : value.series.userOptions.rscInfo.objName} ${(value.series.userOptions.rscInfo.managetypeId === 'E2E') ? value.series.userOptions.rscInfo.rscKeyName : ''}`;
            s += `[${value.series.userOptions.name}]: <b>${convertedValue}</b>`;
          });
          return s;
        };
      }
    },
    makeMinMaxFlag(chartData) {
      if (!chartData || chartData.length < 1) {
        return;
      }

      const minMaxArray = chartData.reduce((accumulator, series, seriesIndex) => {
        const minItemInSeries = minBy(series.data, (item) => item[1]);
        const maxItemInSeries = maxBy(series.data, (item) => item[1]);

        if (isEmpty(minItemInSeries) && isEmpty(maxItemInSeries)) {
          return accumulator;
        }
        const minIndex = series.data.findIndex((item) => item[0] === minItemInSeries[0]);
        const maxIndex = series.data.findIndex((item) => item[0] === maxItemInSeries[0]);
        accumulator.push({
          min: { index: minIndex, value: minItemInSeries, arrayIndex: seriesIndex },
          max: { index: maxIndex, value: maxItemInSeries, arrayIndex: seriesIndex },
        });
        return accumulator;
      }, []);
      if (!isEmpty(minMaxArray) && minMaxArray.lengh > 0) {
        const minItem = minBy(minMaxArray, (item) => item.min.value[1]).min;
        const maxItem = maxBy(minMaxArray, (item) => item.max.value[1]).max;

        const newMinLabel = cloneDeep(defaultMinMaxLabel);
        newMinLabel.x = minItem.value[0];
        newMinLabel.y = minItem.value[1];
        newMinLabel.dataLabels.backgroundColor = '#568ad3';
        newMinLabel.dataLabels.formatter = function formatter() {
          const newY = convertValuByUnit(this.y, isEmpty(chartData[maxItem.arrayIndex].rscInfo) ? '' : chartData[maxItem.arrayIndex].rscInfo.rscUnit, true);
          return `최소 ${newY}`;
        };
        chartData[minItem.arrayIndex].data[minItem.index] = newMinLabel;

        const newMaxLabel = cloneDeep(defaultMinMaxLabel);
        newMaxLabel.x = maxItem.value[0];
        newMaxLabel.y = maxItem.value[1];
        newMinLabel.dataLabels.backgroundColor = '#626992';
        newMaxLabel.dataLabels.formatter = function formatter() {
          const newY = convertValuByUnit(this.y, (isEmpty(chartData[maxItem.arrayIndex].rscInfo) ? '' : chartData[maxItem.arrayIndex].rscInfo.rscUnit), true);
          return `최대 ${newY}`;
        };
        chartData[maxItem.arrayIndex].data[maxItem.index] = newMaxLabel;
      }
    },
    changeChartType(viewType) {
      this.chartOption.chart.type = viewType;
      this.drawChart();
    },
    resize() {
      if (!this.refsChartComponent) {
        return;
      }
      this.refsChartComponent.chart.setSize(this.$el.clientWidth, this.$el.clientHeight);
    },
  },
};
</script>
<style lang="scss">
.perf-hist-chart {
  .min-data-label > span,
  .max-data-label > span {
    top: 1px !important;
  }

  .highcharts-navigator-series {
    .min-data-label,
    .max-data-label {
      display: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/root/components/chart/PerfRootChart.vue